import {
  AddCircleOutline,
  Close,
  Delete,
  Edit,
  ManageAccounts,
} from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import TextLabel from "components/Common/TextLabel/TextLabel";
import { CustomFooter } from "components/RosterConfig/RosterConfigInputs";
import {
  CustomButton,
  StyledDataGrid,
} from "components/RosterConfig/RosterConfigStyle";
import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import ClearIcon from "@mui/icons-material/Clear";
import {
  createReleaseUpdate,
  deleteReleaseUpdate,
  getReleaseUpdates,
  updateReleaseUpdate,
} from "services/userServices";
import { useDispatch } from "react-redux";
import { SnackBarStoreAction } from "Store/SnackBar";
import SnackBar from "components/SnackBar/SnackBar";
import { blue, red } from "@mui/material/colors";
import moment from "moment";
import BackdropComponent from "components/Spinner/Spinner";
import { Transition } from "components/Common/Header/Header";

const today = moment();

export default function AdminManagement(props) {
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [openAddReleaseUpdates, setOpenAddReleaseUpdates] = useState(false);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(today);
  const [description, setDescription] = useState("");
  const [titleError, setTitleError] = useState(false);
  const [dateError, setDateError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [releaseUpdatesId, setReleaseUpdatesId] = useState(0);
  const [isEdit, setIsEdit] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const dispatch = useDispatch();

  const resetStates = () => {
    setTitle("");
    setDate(today);
    setDescription("");
    setTitleError(false);
    setDateError(false);
    setDescriptionError(false);
    setErrorText("");
    setReleaseUpdatesId(0);
    setIsEdit(false);
  };

  useEffect(() => {
    let cancelled = false;

    const loadReleaseUpdates = async () => {
      try {
        await fetchReleaseUpdates();
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch release updates:", error);
        }
      }
    };

    loadReleaseUpdates();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (openAddReleaseUpdates === false) {
      resetStates();
    }
  }, [openAddReleaseUpdates]);

  const handleDeleteReleaseUpdate = async () => {
    setLoading(true);
    const response = await deleteReleaseUpdate(releaseUpdatesId);
    if (response.statusCode === 200) {
      showSnack(`Announcement deleted successfully.`, "success");
      fetchReleaseUpdates();
    } else {
      showSnack("Internal error occured", "error");
    }
    setReleaseUpdatesId(0);
    setIsDeleteDialogOpen(false);
    setLoading(false);
  };

  const handleEdit = (params) => {
    const { id } = params;
    const data = releaseUpdateRows.find((item) => item?.id === id);
    if (data) {
      setTitle(data.Title);
      setDate(data.Date);
      setDescription(data.Description);
      setIsEdit(true);
      setReleaseUpdatesId(data.ReleaseUpdatesId);
      setOpenAddReleaseUpdates(true);
    }
  };

  const handleDelete = (params) => {
    const { id } = params;
    setReleaseUpdatesId(id);
    setIsDeleteDialogOpen(true);
  };

  const renderActionsCell = (params) => {
    return (
      <Grid container>
        <Grid item xs={5}>
          <IconButton onClick={() => handleEdit(params)}>
            <Edit sx={{ color: blue[500] }} />
          </IconButton>
        </Grid>
        <Grid item xs={1}></Grid>
        <Grid item xs={5}>
          <IconButton onClick={() => handleDelete(params)}>
            <Delete sx={{ color: red[400] }} />
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  const columns = [
    // {
    //   field: "id",
    //   headerName: "Id",
    //   flex: 0.05,
    //   sortable: false,
    // },
    {
      field: "Title",
      headerName: "Title",
      flex: 0.4,
      sortable: false,
    },
    {
      field: "Date",
      headerName: "Date",
      flex: 0.15,
      sortable: false,

      valueFormatter: ({ value }) => moment(value).format("MM-DD-YYYY"),
    },
    {
      field: "Description",
      headerName: "Description",
      flex: 1,
      sortable: false,
    },
    {
      field: "ReleaseNotesId",
      headerName: "Actions",
      flex: 0.15,
      sortable: false,
      renderCell: renderActionsCell,
    },
  ];
  const prepareCreatePayload = () => ({
    title: title,
    description: description,
    date: date,
    createdBy: localStorage.getItem("User"),
  });

  const prepareEditPayload = () => ({
    releaseUpdatesId: releaseUpdatesId,
    releaseUpdateDetails: prepareCreatePayload(),
  });
  const [releaseUpdateRows, setReleaseUpdateRows] = useState([]);

  const showSnack = (message, severity) => {
    const obj = {
      open: true,
      message: message,
      severity: severity,
    };
    dispatch(SnackBarStoreAction.updateSnack(obj));
  };

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const validateFields = () => {
    if (title === "") {
      setTitleError(true);
      setErrorText("Announcement title is required.");
      return false;
    } else if (date === "") {
      setDateError(true);
      setErrorText("Announcement date is required.");
      return false;
    } else if (description === "") {
      setDescriptionError(true);
      setErrorText("Announcement description is required.");
      return false;
    }
    return true;
  };

  const fetchReleaseUpdates = async () => {
    setLoading(true);
    const response = await getReleaseUpdates();

    if (response.statusCode === 200) {
      setReleaseUpdateRows(
        response.data.map((item) => ({ ...item, id: item?.ReleaseUpdatesId })),
      );
    } else {
      showSnack("Unable to fetch announcements.", "error");
    }
    setLoading(false);
  };

  const handleAddReleaseUpdate = async () => {
    if (validateFields()) {
      setLoading(true);
      const payload = isEdit ? prepareEditPayload() : prepareCreatePayload();
      const response = isEdit
        ? await updateReleaseUpdate(payload)
        : await createReleaseUpdate(payload);
      if (response.statusCode === 200) {
        showSnack(
          `Announcement ${isEdit ? "updated" : "added"} successfully.`,
          "success",
        );

        fetchReleaseUpdates();
      } else {
        showSnack("Internal error occured", "error");
      }
      setOpenAddReleaseUpdates(false);
      setLoading(false);
    }
  };

  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <Grid container>
        <Grid item xs={12}>
          <Paper elevation={1}>
            <Grid container>
              <Grid item xs={0.3} sx={{ padding: 1.5 }}>
                <ManageAccounts fontSize="large" sx={{ color: "#3299ff" }} />
              </Grid>
              <Grid item xs={10} sx={{ padding: 2 }}>
                <TextLabel
                  text={"Admin Management"}
                  header
                  fontSize={18}
                ></TextLabel>
              </Grid>
              <Grid item xs={1.2}></Grid>
              <Grid item xs={0.5} sx={{ padding: 2 }}>
                <IconButton onClick={props.onClose}>
                  <Close sx={{ color: "#3299ff" }} />
                </IconButton>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={3} sx={{ padding: 2 }}>
          <TextLabel
            text={"Release Announcements"}
            header
            fontSize={18}
          ></TextLabel>
        </Grid>
        <Grid item xs={9}></Grid>
        <Grid item xs={12} sx={{ paddingLeft: 2 }}>
          <CustomButton
            variant="contained"
            sx={{ backgroundColor: "#3299FF" }}
            onClick={() => {
              setOpenAddReleaseUpdates(true);
            }}
            startIcon={<AddCircleOutline />}
          >
            Create
          </CustomButton>
        </Grid>
        <Grid item xs={12} sx={{ padding: 2 }}>
          <StyledDataGrid
            sx={{
              fontFamily: "Urbanist, Verdana, Arial, sans-serif",
              letterSpacing: "0.10rem",
            }}
            autoHeight
            rows={releaseUpdateRows}
            columns={columns}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[5, 10, 25]}
            disableSelectionOnClick
            disableColumnMenu
            components={{
              Footer: () => (
                <CustomFooter
                  pageSize={pageSize}
                  displayText={"Total records:"}
                  rowCount={releaseUpdateRows.length}
                  rowsPerPageLabel={"Rows per page"}
                />
              ),
            }}
          />
        </Grid>
      </Grid>
      <Dialog
        open={openAddReleaseUpdates}
        fullWidth
        maxWidth={"lg"}
        TransitionComponent={Transition}
        onClose={() => setOpenAddReleaseUpdates(false)}
      >
        {loading ? <BackdropComponent /> : null}
        <Grid container>
          <Grid item xs={11.5} sx={{ padding: 2 }}>
            <TextLabel
              text={`${isEdit ? "Update" : "Create"} Release Announcement`}
              header
              fontSize={16}
            ></TextLabel>
          </Grid>
          <Grid item xs={0.5} sx={{ paddingTop: 2 }}>
            <IconButton onClick={() => setOpenAddReleaseUpdates(false)}>
              <Close sx={{ color: "#3299ff" }} />
            </IconButton>
          </Grid>
          <Grid item xs={6} sx={{ padding: 2 }}>
            <Grid container>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                    letterSpacing: "0.10rem",
                    fontWeight: "bold",
                    color: "#3F5765",
                    paddingBottom: 1,
                  }}
                >
                  {"Announcement title *"}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  error={titleError}
                  variant="outlined"
                  size="medium"
                  inputProps={{ maxLength: 200 }}
                  placeholder="Maximum of 200 characters."
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    if (e.target.value !== "" && titleError) {
                      setTitleError(false);
                      setErrorText("");
                    }
                  }}
                  helperText={
                    <Box
                      component={"span"}
                      sx={{
                        color: title?.length === 200 ? "red" : "inherit",
                      }}
                    >{`${title ? title.length : 0}/200`}</Box>
                  }
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={0.5}></Grid>
          <Grid item xs={4} sx={{ padding: 2 }}>
            <Grid container>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                    letterSpacing: "0.10rem",
                    fontWeight: "bold",
                    color: "#3F5765",
                    paddingBottom: 1,
                  }}
                >
                  {"Announcement date *"}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={moment(date) || null}
                    onChange={(newValue) => {
                      if (
                        newValue &&
                        newValue instanceof Date &&
                        !isNaN(newValue)
                      ) {
                        setDate(format(newValue, "yyyy-MM-dd"));
                        if (dateError) {
                          setDateError(false);
                          setErrorText("");
                        }
                      }
                    }}
                    format="yyyy-MM-dd"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        error={dateError}
                        fullWidth
                        size="medium"
                        variant="outlined"
                        name="Anouncement date"
                        //error={errorProductionDate}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {date && (
                                <Tooltip title={"Clear"}>
                                  <IconButton
                                    onClick={() => setDate(today)}
                                    edge="end"
                                  >
                                    <ClearIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {params.InputProps?.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={1.5}></Grid>
          <Grid item xs={12} sx={{ padding: 2 }}>
            <Grid container>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                    letterSpacing: "0.10rem",
                    fontWeight: "bold",
                    color: "#3F5765",
                    paddingBottom: 1,
                  }}
                >
                  {"Description *"}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  error={descriptionError}
                  required
                  multiline
                  maxRows={10}
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    if (e.target.value !== "" && descriptionError) {
                      setDescriptionError(false);
                      setErrorText("");
                    }
                  }}
                  inputProps={{ maxLength: 4000 }}
                  placeholder="Maximum of 4000 characters."
                  helperText={
                    <Box
                      component={"span"}
                      sx={{
                        color: description?.length === 4000 ? "red" : "inherit",
                      }}
                    >{`${description ? description.length : 0}/4000`}</Box>
                  }
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={6} sx={{ padding: 2 }}>
            {errorText !== "" && (
              <Typography
                sx={{
                  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                  color: red[700],
                  letterSpacing: "0.10rem",
                }}
              >
                {errorText}
              </Typography>
            )}
          </Grid>
          <Grid item xs={6}></Grid>

          <Grid item xs={1} sx={{ padding: 2 }}>
            <CustomButton
              variant="contained"
              sx={{ backgroundColor: "#3299FF" }}
              onClick={handleAddReleaseUpdate}
            >
              {isEdit ? "Update" : "Create"}
            </CustomButton>
          </Grid>
          <Grid item xs={1} sx={{ padding: 2 }}>
            <CustomButton
              variant="outlined"
              sx={{ color: "#3299FF" }}
              onClick={() => setOpenAddReleaseUpdates(false)}
            >
              {"Cancel"}
            </CustomButton>
          </Grid>
          <Grid item xs={10}></Grid>
        </Grid>
      </Dialog>
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        TransitionComponent={Transition}
      >
        {loading ? <BackdropComponent /> : null}
        <DialogTitle>{"Delete Release Announcement"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete release announcement ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <CustomButton
            variant="contained"
            sx={{ backgroundColor: "#3299FF" }}
            onClick={handleDeleteReleaseUpdate}
          >
            Delete
          </CustomButton>

          <CustomButton
            sx={{ color: "#3299FF" }}
            variant="outlined"
            onClick={() => setIsDeleteDialogOpen(false)}
          >
            Cancel
          </CustomButton>
        </DialogActions>
      </Dialog>
      <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
    </>
  );
}
